import React, { useEffect, useState } from "react";
import { DownloadSimple, X, DeviceMobile } from "@phosphor-icons/react";

const DISMISS_KEY = "firewatch:pwa-install-dismissed-at";
const DISMISS_TTL_DAYS = 14;

function wasRecentlyDismissed() {
  const v = localStorage.getItem(DISMISS_KEY);
  if (!v) return false;
  const ms = Date.now() - parseInt(v, 10);
  return ms < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
}

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari doesn't fire beforeinstallprompt — show static "Share → Add to Home Screen" hint
    if (isIOS()) {
      const t = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    } else {
      dismiss();
    }
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-[1500] bg-tac-bg/95 backdrop-blur-xl border border-tac-borderBright rounded-sm shadow-2xl"
    >
      <div className="p-4 flex gap-3 items-start">
        <div className="shrink-0 w-10 h-10 rounded-sm bg-tac-primary/15 border border-tac-primary/30 flex items-center justify-center">
          <DeviceMobile size={20} weight="duotone" className="text-tac-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-primary mb-1">
            Install Firewatch
          </div>
          <div className="text-xs text-tac-text leading-snug mb-2">
            {iosHint
              ? "Add Firewatch to your Home Screen for faster access and reliable push alerts."
              : "Install Firewatch on your device for one-tap access and reliable proximity alerts."}
          </div>
          {iosHint ? (
            <div className="text-[14px] text-tac-dim font-mono-tac">
              Tap <span className="text-tac-primary font-bold">Share</span> →{" "}
              <span className="text-tac-primary font-bold">Add to Home Screen</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                data-testid="pwa-install-btn"
                onClick={install}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-tac-primary text-white rounded-sm hover:bg-tac-primary/90"
              >
                <DownloadSimple size={12} weight="bold" /> Install
              </button>
              <button
                data-testid="pwa-install-later"
                onClick={dismiss}
                className="px-3 py-1.5 text-[15px] font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 text-tac-dim rounded-sm"
              >
                Later
              </button>
            </div>
          )}
        </div>
        <button
          data-testid="pwa-install-close"
          onClick={dismiss}
          className="text-tac-faint hover:text-tac-text p-1 -m-1"
          aria-label="Dismiss"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
